import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Form, Button, Table, Badge, Alert } from "react-bootstrap";
import { FaPlus, FaEdit, FaTrash, FaArrowLeft, FaSearch, FaProjectDiagram } from "react-icons/fa";
import { useProyectos } from "../../../services/hooks/useProyectos";
import ModalProyecto from "../../../components/modals/ModalProyecto";
import { Modal } from "antd";

const GestionProyectos = () => {
  const navigate = useNavigate();


  // Usar el hook personalizado de proyectos
  const {
    proyectos,
    loading,
    createProyecto,
    updateProyecto,
    deleteProyecto: deleteProyectoApi,
    fetchProyectos
  } = useProyectos();

  const [state, setState] = useState({
    proyectoSeleccionado: null,
    searchTerm: "",
  });

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' o 'edit'
  const [proyectoToEdit, setProyectoToEdit] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [proyectoToDelete, setProyectoToDelete] = useState(null);

  // Cargar proyectos al montar el componente
  useEffect(() => {
    fetchProyectos();
  }, [fetchProyectos]);

  // Actualizar proyecto seleccionado cuando la lista de proyectos se actualiza
  // Esto asegura que siempre tengamos la versión más reciente del proyecto seleccionado
  useEffect(() => {
    if (state.proyectoSeleccionado?.id) {
      const proyectoActualizado = proyectos.find(p => p.id === state.proyectoSeleccionado.id);
      if (proyectoActualizado) {
        // Comparar si los datos realmente cambiaron para evitar actualizaciones innecesarias
        const datosCambiaron = JSON.stringify(proyectoActualizado) !== JSON.stringify(state.proyectoSeleccionado);
        if (datosCambiaron) {
          setState(prev => ({ ...prev, proyectoSeleccionado: proyectoActualizado }));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proyectos]);

  const handleSearch = (event) => {
    setState(prev => ({ ...prev, searchTerm: event.target.value }));
  };

  const handleProyectoSelect = (proyecto) => {
    setState(prev => ({ 
      ...prev, 
      proyectoSeleccionado: prev.proyectoSeleccionado?.id === proyecto.id ? null : proyecto 
    }));
  };

  const handleDelete = (id) => {
    if (!id) {
      toast.error("Por favor seleccione un proyecto para eliminar", {
        position: "bottom-right",
        autoClose: 2000
      });
      return;
    }

    setProyectoToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!proyectoToDelete) return;

    // Cerrar modal inmediatamente
    setShowDeleteModal(false);
    
    try {
      await deleteProyectoApi(proyectoToDelete);
      // El hook ya maneja la actualización de la lista y los toasts
    } catch (error) {
      console.error("Error al eliminar proyecto:", error);
      // El error ya se maneja en el hook con toast
    } finally {
      setProyectoToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setProyectoToDelete(null);
  };

  const handleOpenCreateModal = () => {
    setModalMode('create');
    setProyectoToEdit(null);
    setShowModal(true);
  };

  const handleOpenEditModal = () => {
    if (state.proyectoSeleccionado) {
      // Buscar el proyecto actualizado en la lista para obtener los datos más recientes
      const proyectoActualizado = proyectos.find(p => p.id === state.proyectoSeleccionado.id);
      
      if (proyectoActualizado) {
        setModalMode('edit');
        setProyectoToEdit(proyectoActualizado);
        // Actualizar también el proyecto seleccionado con los datos actualizados
        setState(prev => ({ ...prev, proyectoSeleccionado: proyectoActualizado }));
        setShowModal(true);
      } else {
        toast.error("No se pudo encontrar el proyecto actualizado", {
          position: "bottom-right",
          autoClose: 2000
        });
      }
    } else {
      toast.error("Por favor seleccione un proyecto para editar", {
        position: "bottom-right",
        autoClose: 2000
      });
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setProyectoToEdit(null);
  };

  const handleSaveProyecto = async (proyectoData) => {
    try {
      if (modalMode === 'create') {
        await createProyecto(proyectoData);
      } else {
        await updateProyecto(proyectoToEdit.id, proyectoData);
        // El useEffect se encargará de actualizar proyectoSeleccionado cuando proyectos se actualice
      }
      
      // Cerrar el modal
      handleCloseModal();
    } catch (error) {
      console.error('Error al guardar proyecto:', error);
      // El error ya se maneja en el hook con toast
    }
  };

  const filteredProyectos = proyectos.filter((proyecto) =>
    proyecto.nombre.toLowerCase().includes(state.searchTerm.toLowerCase())
  );

  return (
    <>
      {/* Digital Harmony Styles para Mantenimiento Proyectos */}
      <style>
        {`
          /* Fondo degradado */
          .dh-maintenance-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
          }
          
          /* Header principal */
          .dh-main-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem 0;
            margin-bottom: 2rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }
          
          .dh-main-title {
            font-size: 2.2rem;
            font-weight: 700;
            margin: 0;
          }
          
          .dh-main-subtitle {
            font-size: 1.1rem;
            opacity: 0.9;
            margin: 0.5rem 0 0 0;
          }
          
          /* Cards con Digital Harmony */
          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
          }
          
          .dh-card-header {
            border-radius: 20px 20px 0 0;
            padding: 1.5rem 2rem;
            border: none;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            font-weight: 700;
          }
          
          /* Botones Digital Harmony */
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(17, 153, 142, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-primary:hover {
            background: linear-gradient(135deg, #38ef7d 0%, #2dd4bf 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(17, 153, 142, 0.4);
            color: white;
          }
          
          .btn-dh-warning {
            background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(251, 191, 36, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-warning:hover {
            background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(251, 191, 36, 0.4);
            color: white;
          }
          
          .btn-dh-danger {
            background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(239, 68, 68, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-danger:hover {
            background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(239, 68, 68, 0.4);
            color: white;
          }
          
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(107, 114, 128, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-secondary:hover {
            background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(107, 114, 128, 0.4);
            color: white;
          }
          
          /* Campos de formulario modernos */
          .dh-form-control {
            border: 2px solid #e5e7eb;
            border-radius: 10px;
            padding: 12px 16px;
            font-size: 1rem;
            transition: all 0.3s ease;
            background: rgba(255, 255, 255, 0.9);
          }
          
          .dh-form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
            background: white;
          }
          
          /* Enhanced Table */
          .dh-enhanced-table {
            border-collapse: separate;
            border-spacing: 0;
            border-radius: 15px;
            overflow: hidden;
            box-shadow: 0 4px 15px rgba(0,0,0,0.05);
            background: white;
          }
          
          .dh-enhanced-table thead th {
            background: #374151 !important;
            color: white !important;
            font-weight: 700 !important;
            font-size: 0.875rem !important;
            text-transform: uppercase !important;
            padding: 1rem 0.75rem !important;
            border: none !important;
            position: sticky !important;
            top: 0 !important;
            z-index: 10 !important;
          }
          
          .dh-enhanced-table tbody td {
            padding: 0.75rem;
            border: none;
            border-bottom: 1px solid #f3f4f6;
            font-size: 0.9rem;
            vertical-align: middle;
          }
          
          .dh-enhanced-table tbody tr:hover {
            background: linear-gradient(135deg, #f9fafb 0%, rgba(102, 126, 234, 0.02) 100%) !important;
          }
          
          .dh-enhanced-table tbody tr.selected {
            background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%) !important;
            border-left: 4px solid #667eea;
          }
          
          /* Checkbox personalizado */
          .dh-checkbox {
            width: 20px;
            height: 20px;
            border: 2px solid #d1d5db;
            border-radius: 6px;
            background: white;
            cursor: pointer;
            transition: all 0.2s ease;
          }
          
          .dh-checkbox:checked {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-color: #667eea;
          }
        `}
      </style>

      <div className="dh-maintenance-bg">
        <ToastContainer />
        
        <div className="dh-main-header">
          <Container>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1 className="dh-main-title">
                  <FaProjectDiagram className="me-3" />
                  Mantenimiento de Proyectos
                </h1>
                <p className="dh-main-subtitle">Gestión y administración de proyectos del sistema</p>
              </div>
              <Button 
                className="btn-dh-secondary d-flex align-items-center"
                onClick={() => navigate("/home")}
              >
                <FaArrowLeft className="me-2" />
                Volver
              </Button>
            </div>
          </Container>
        </div>

        <Container fluid className="py-4">
          <Row>
            <Col lg={12}>
              <Card className="dh-card">
                <Card.Header className="dh-card-header">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Lista de Proyectos</h5>
                    <div className="d-flex gap-2">
                      <Button
                        onClick={handleOpenCreateModal}
                        className="btn-dh-primary d-flex align-items-center"
                      >
                        <FaPlus className="me-2" />
                        Nuevo Proyecto
                      </Button>
                      <Button
                        className="btn-dh-warning d-flex align-items-center"
                        onClick={handleOpenEditModal}
                      >
                        <FaEdit className="me-2" />
                        Editar
                      </Button>
                      <Button
                        onClick={() => handleDelete(state.proyectoSeleccionado?.id)}
                        className="btn-dh-danger d-flex align-items-center"
                      >
                        <FaTrash className="me-2" />
                        Eliminar
                      </Button>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body style={{ padding: '2rem' }}>
                  <div className="mb-4">
                    <div className="position-relative">
                      <Form.Control
                        type="search"
                        className="dh-form-control"
                        placeholder="Buscar proyecto..."
                        value={state.searchTerm}
                        onChange={handleSearch}
                        style={{ paddingLeft: '3rem' }}
                      />
                      <FaSearch 
                        className="position-absolute" 
                        style={{ 
                          left: '1rem', 
                          top: '50%', 
                          transform: 'translateY(-50%)', 
                          color: '#6b7280' 
                        }} 
                      />
                    </div>
                  </div>

                  <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    <Table className="dh-enhanced-table">
                      <thead>
                        <tr>
                          <th style={{ width: "50px" }}></th>
                          <th>Nombre</th>
                          <th>País</th>
                          <th>Departamento</th>
                          <th>Provincia</th>
                          <th>Distrito</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan="6" className="text-center py-5">
                              <div className="d-flex flex-column align-items-center">
                                <div className="spinner-border text-primary mb-3" role="status">
                                  <span className="visually-hidden">Cargando...</span>
                                </div>
                                <span className="text-muted">Cargando proyectos...</span>
                              </div>
                            </td>
                          </tr>
                        ) : filteredProyectos.length > 0 ? (
                          filteredProyectos.map((proyecto) => (
                            <tr 
                              key={proyecto.id}
                              className={state.proyectoSeleccionado?.id === proyecto.id ? 'selected' : ''}
                              style={{ cursor: 'pointer' }}
                              onClick={() => handleProyectoSelect(proyecto)}
                            >
                              <td>
                                <Form.Check
                                  type="checkbox"
                                  className="dh-checkbox"
                                  checked={state.proyectoSeleccionado?.id === proyecto.id}
                                  onChange={() => {}}
                                />
                              </td>
                              <td style={{ fontWeight: '600', color: '#374151' }}>{proyecto.nombre}</td>
                              <td>{proyecto.pais}</td>
                              <td>{proyecto.departamento}</td>
                              <td>{proyecto.provincia}</td>
                              <td>{proyecto.distrito}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="6" className="text-center py-5">
                              <Alert variant="info" className="mb-0">
                                <FaProjectDiagram className="me-2" />
                                No se encontraron proyectos
                              </Alert>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <ModalProyecto
        show={showModal}
        onHide={handleCloseModal}
        onSave={handleSaveProyecto}
        proyecto={proyectoToEdit}
      />

      <Modal
        title="Confirmar eliminación"
        open={showDeleteModal}
        onOk={confirmDelete}
        onCancel={cancelDelete}
        okText="Eliminar"
        cancelText="Cancelar"
        okType="danger"
        centered
      >
        <p>¿Estás seguro de que quieres eliminar este proyecto?</p>
        <p>Esta acción no se puede deshacer.</p>
      </Modal>
    </>
  );
};

export default GestionProyectos;
